/** 救援推送页常量（保持与原 RescuePush.js 一致） */

/** 5 分钟内为在线 */
export const ONLINE_TIMEOUT = 5 * 60 * 1000;

export const STORAGE_KEYS = {
  targets: 'rescue_push_targets',
  tracks: 'rescue_push_tracks'
};

export const ICONS = [
  { value: 'flag', label: '旗帜' },
  { value: 'pin', label: '图钉' },
  { value: 'star', label: '星标' },
  { value: 'sos', label: 'SOS' },
  { value: 'person', label: '人员' },
  { value: 'custom', label: '自定义' }
];

export const TRACK_PREVIEW_ID = 'track-preview';

export const DEFAULT_TARGET_FORM = {
  org_names: [],
  group_names: [],
  user_names: [],
  lat: '',
  lng: '',
  icon: '旗帜',
  message: '',
  map_link: '',
  level: '普通',
  to_all: false,
  expires_at: '',
  mission_id: '',
  mission_group_id: '',
  mission_group_name: ''
};

export const DEFAULT_TRACK_FORM = {
  org_names: [],
  group_names: [],
  user_names: [],
  message: '',
  icon: '旗帜',
  to_all: false
};


/** 本地时区 datetime-local 值（YYYY-MM-DDTHH:mm） */
export function toDatetimeLocalValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 默认有效时间：当前 + 24 小时（本地输入框） */
export function defaultExpiresAtLocalInput(fromDate = new Date()) {
  const base = fromDate instanceof Date ? fromDate : new Date(fromDate);
  return toDatetimeLocalValue(new Date(base.getTime() + 24 * 60 * 60 * 1000));
}

/** datetime-local → ISO（按浏览器本地时区解释） */
export function datetimeLocalToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** 是否已过有效期（无 expires_at 视为不过期，兼容旧数据） */
export function isRescueTargetExpired(item, now = Date.now()) {
  if (!item || item.expires_at == null || item.expires_at === '') return false;
  const t = new Date(item.expires_at).getTime();
  return Number.isFinite(t) && t <= now;
}


/** 界面时间：不显示秒 */
export function formatDateTimeNoSeconds(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 有效时长展示：XX时XX分（按 created_at → expires_at；无截止返回 —） */
export function formatDurationHoursMinutes(createdAt, expiresAt) {
  if (!expiresAt) return '—';
  const start = createdAt ? new Date(createdAt).getTime() : NaN;
  const end = new Date(expiresAt).getTime();
  if (!Number.isFinite(end)) return '—';
  const base = Number.isFinite(start) ? start : end;
  let totalMin = Math.max(0, Math.round((end - base) / 60000));
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return `${hours}时${minutes}分`;
}

export function durationPartsFromTarget(item) {
  if (!item?.expires_at) return { hours: 24, minutes: 0 };
  const start = item.created_at ? new Date(item.created_at).getTime() : Date.now();
  const end = new Date(item.expires_at).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return { hours: 24, minutes: 0 };
  const totalMin = Math.max(1, Math.round((end - start) / 60000));
  return { hours: Math.floor(totalMin / 60), minutes: totalMin % 60 };
}

export function shortenMessage(message, max = 18) {
  const text = String(message || '').trim() || '无消息';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function asNameList(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || '').trim()).filter(Boolean);
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      if (Array.isArray(parsed)) return parsed.map((x) => String(x || '').trim()).filter(Boolean);
    } catch (_) { /* plain */ }
    return t.split(/[,，;；]/).map((x) => x.trim()).filter(Boolean);
  }
  return [];
}

/** 可见范围文案：所有人 / 指定组织·组·人 */
export function formatVisibilityScope(item) {
  if (!item) return '未知';
  const toAll = item.to_all === true || item.to_all === 't' || item.to_all === 1 || item.to_all === '1';
  if (toAll) return '所有人可见';
  const orgs = asNameList(item.org_names).concat(item.org_name ? [String(item.org_name)] : []);
  const groups = asNameList(item.group_names).concat(item.group_name ? [String(item.group_name)] : []);
  const users = asNameList(item.user_names).concat(item.user_name ? [String(item.user_name)] : []);
  const uniq = (arr) => [...new Set(arr.filter(Boolean))];
  const parts = [];
  const o = uniq(orgs);
  const g = uniq(groups);
  const u = uniq(users);
  if (o.length) parts.push(`组织：${o.join('、')}`);
  if (g.length) parts.push(`小组：${g.join('、')}`);
  if (u.length) parts.push(`队员：${u.join('、')}`);
  if (!parts.length) return '指定范围（未写接收者）';
  return parts.join('；');
}
